import { useCallback, useEffect, useState } from 'react';
import { clearAllVouchers, clearHasAccessData, clearPendingDomains, clearPermissioningCache, clearTokens, clearUser, persistor, resetAllApiCaches, useAppDispatch, } from '@ibk/shared-config';
import { clearDatadogUser, setDatadogUser } from '@ibk/shared-datadog';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { FeatureFlagUseCase, LoginErrorsType } from '@ibk/shared-types';
import { localStorageService, sessionStorageService, StorageKeys } from '@ibk/shared-utils';
import { useLazyValidatePasswordQuery, useLoginMutation, useServerLogoutMutation } from '../services/authApi';
import { resendDeviceTokenApi, saveDeviceApi } from '../services/deviceApi';
import { initializeDeviceInterceptor } from '../services/deviceInterceptor';
import { clearDeviceDataFromSession, getOrCreateDeviceId, getRegisteredDevice, isDeviceValidatedInSession, saveRegisteredDevice, setDeviceIdInSession, setDeviceValidatedInSession, } from '../services/deviceStorage';
import { TokenService } from '../services/tokenApi';
import { getEmailFromToken, getPartyIdFromToken, getPersonIdFromToken, getPreferredUsernameFromToken, } from '../utils/jwtUtils';
const { AuthToken, AuthRefreshToken } = StorageKeys;
export function useAuth() {
    const dispatch = useAppDispatch();
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [codeAttempts, setCodeAttempts] = useState(0);
    const [loginMutation] = useLoginMutation();
    const [serverLogoutMutation] = useServerLogoutMutation();
    const [validatePasswordQuery] = useLazyValidatePasswordQuery();
    const { isFeatureFlagWhiteListed, getFeatureFlags } = useFeatureFlag(undefined, {
        autoLoad: false,
        isLoggedIn: false,
    });
    const handleLoginErrorMessages = (error) => {
        const result = error?.details?.result;
        const isMaintenanceCode = [error?.code, result?.code, result?.message, result?.error].includes(LoginErrorsType.MAINTENANCE);
        if (error?.status === 503 && isMaintenanceCode) {
            return LoginErrorsType.MAINTENANCE;
        }
        const errorMessage = error?.details?.result?.message;
        if (errorMessage === LoginErrorsType.INVALID_CREDENTIALS) {
            return LoginErrorsType.INVALID_CREDENTIALS;
        }
        if (errorMessage === LoginErrorsType.CREDENTIAL_ROLE_INVALID) {
            return LoginErrorsType.CREDENTIAL_ROLE_INVALID;
        }
        return LoginErrorsType.GENERIC;
    };
    const handlePasswordValidationErrorMessages = (errorMessage) => {
        if (errorMessage === LoginErrorsType.MISSING_CREDENTIALS) {
            return LoginErrorsType.MISSING_CREDENTIALS;
        }
        if (errorMessage === LoginErrorsType.INVALID_CREDENTIALS_FOR_USER_PARTY_RELATIONSHIP) {
            return LoginErrorsType.INVALID_CREDENTIALS_FOR_USER_PARTY_RELATIONSHIP;
        }
        if (errorMessage === LoginErrorsType.AUTH_TOKEN_EXPIRED) {
            return LoginErrorsType.AUTH_TOKEN_EXPIRED;
        }
        return LoginErrorsType.GENERIC;
    };
    const clearReduxState = useCallback(() => {
        localStorageService.removeItem(AuthToken);
        localStorageService.removeItem(AuthRefreshToken);
        dispatch(clearTokens());
        dispatch(clearUser());
        dispatch(clearAllVouchers());
        dispatch(clearHasAccessData());
        resetAllApiCaches(dispatch);
    }, [dispatch]);
    useEffect(() => {
        initializeDeviceInterceptor();
        getOrCreateDeviceId();
        const staleCheckKey = 'stale_token_checked';
        const hasCheckedStale = sessionStorage.getItem(staleCheckKey);
        if (!hasCheckedStale) {
            sessionStorage.setItem(staleCheckKey, 'true');
            const storedToken = localStorageService.getItemSync(AuthToken);
            if (storedToken && !isDeviceValidatedInSession()) {
                localStorageService.removeItem(AuthToken);
                setToken(null);
                return;
            }
        }
        const syncToken = () => {
            const currentToken = localStorageService.getItemSync(AuthToken);
            setToken(currentToken || null);
        };
        syncToken();
        window.addEventListener('storage', syncToken);
        return () => window.removeEventListener('storage', syncToken);
    }, []);
    useEffect(() => {
        if (token && !data) {
            const personId = getPersonIdFromToken(token);
            const email = getEmailFromToken(token);
            const preferredUsername = getPreferredUsernameFromToken(token);
            if (personId && email && preferredUsername) {
                setData({ personId, email, preferredUsername });
            }
            else {
                setData(null);
            }
        }
    }, [token, data]);
    const login = useCallback(async (documentNumber, password) => {
        clearReduxState();
        setLoading(true);
        setError(null);
        try {
            const apiResponse = await loginMutation({ documentNumber, password }).unwrap();
            const personId = getPersonIdFromToken(apiResponse.result.jwtToken);
            const partyId = getPartyIdFromToken(apiResponse.result.jwtToken);
            const featureFlagsData = await getFeatureFlags(true);
            const hasNewAccess = isFeatureFlagWhiteListed(FeatureFlagUseCase.NEW_IBKS_ACCESS_CONTROL, personId ?? undefined, featureFlagsData);
            if (!hasNewAccess) {
                setError(LoginErrorsType.FEATURE_NOT_ENABLED);
                return;
            }
            setToken(apiResponse.result.jwtToken);
            localStorageService.setItemSync(AuthToken, apiResponse.result.jwtToken);
            const storedToken = apiResponse.result.jwtToken;
            const email = getEmailFromToken(storedToken || '');
            const preferredUsername = getPreferredUsernameFromToken(storedToken || '');
            if (!personId)
                throw new Error('Token inválido: personId não encontrado');
            if (!email)
                throw new Error('Token inválido: email não encontrado');
            if (!preferredUsername)
                throw new Error('Token inválido: preferred_username não encontrado');
            const registeredDevice = await getRegisteredDevice(personId);
            if (registeredDevice?.deviceId) {
                setDeviceIdInSession(registeredDevice.deviceId);
                setDeviceValidatedInSession(registeredDevice.validated);
            }
            setData({ personId, email, preferredUsername });
            setDatadogUser(personId, partyId);
        }
        catch (err) {
            const errorMessage = handleLoginErrorMessages(err);
            setError(errorMessage);
            setToken(null);
            localStorageService.removeItem(AuthToken);
        }
        finally {
            setLoading(false);
        }
    }, [loginMutation, getFeatureFlags, isFeatureFlagWhiteListed, clearReduxState]);
    const sendToken = useCallback(async (channel) => {
        if (!data) {
            setError('Dados de 2FA não encontrados');
            return;
        }
        setLoading(true);
        setError(null);
        setCodeAttempts(0);
        try {
            const registeredDevice = await getRegisteredDevice(data.personId);
            if (!registeredDevice?.deviceId) {
                const deviceResult = await saveDeviceApi(channel, data.personId);
                await saveRegisteredDevice(data.personId, deviceResult.id, false);
                return deviceResult;
            }
            else {
                const deviceId = registeredDevice?.deviceId ?? getOrCreateDeviceId();
                const result = await resendDeviceTokenApi(channel, data.personId, deviceId);
                return result;
            }
        }
        catch (err) {
            if (err instanceof Error && err.message === 'DEVICE_NOT_FOUND') {
                setError('Dispositivo não encontrado. Registre um dispositivo novamente.');
                clearDeviceDataFromSession();
                setData(null);
                return;
            }
            setError(err?.message ?? 'Erro ao enviar token');
            throw err;
        }
        finally {
            setLoading(false);
        }
    }, [data]);
    const validateToken = useCallback(async (token) => {
        if (!data) {
            setError('Dados de 2FA não encontrados');
            return false;
        }
        setLoading(true);
        setError(null);
        try {
            const registeredDevice = await getRegisteredDevice(data.personId);
            const deviceId = registeredDevice?.deviceId ?? getOrCreateDeviceId();
            const result = await TokenService.validateToken({
                token,
                personId: data.personId,
                deviceId,
            });
            if (result.result) {
                setData(null);
                setDeviceValidatedInSession(true);
                setCodeAttempts(0);
                return true;
            }
            if (result.status === 401) {
                setError('Token expirado. Faça login novamente.');
                setData(null);
                setToken(null);
                localStorageService.removeItem(AuthToken);
                return false;
            }
            if (result.status === 400) {
                setCodeAttempts(prev => prev + 1);
                setError('Código incorreto. Verifique e tente novamente.');
                return false;
            }
            setError('Erro de conexão. Tente novamente.');
            return false;
        }
        catch (err) {
            const errorMessage = err?.message || 'Erro ao validar token';
            setError(errorMessage);
            return false;
        }
        finally {
            setLoading(false);
        }
    }, [data]);
    const registerDeviceAndSave = useCallback(async (channel) => {
        if (!data) {
            setError('Dados de 2FA não encontrados');
            return null;
        }
        setLoading(true);
        setError(null);
        try {
            const deviceResult = await saveDeviceApi(channel, data.personId);
            await saveRegisteredDevice(data.personId, deviceResult.id, false);
            return deviceResult;
        }
        catch (err) {
            setError(err?.message || 'Erro ao registrar dispositivo');
            return null;
        }
        finally {
            setLoading(false);
        }
    }, [data]);
    const logout = useCallback(async () => {
        try {
            await serverLogoutMutation().unwrap();
        }
        catch {
        }
        clearReduxState();
        setToken(null);
        setData(null);
        dispatch(clearPendingDomains());
        dispatch(clearPermissioningCache());
        localStorageService.clear();
        sessionStorageService.clear();
        persistor.purge();
        clearDatadogUser();
    }, [clearReduxState, dispatch, serverLogoutMutation]);
    const validatePassword = useCallback(async (password, selectedCompanyId) => {
        if (!data) {
            return false;
        }
        setError(null);
        setLoading(true);
        try {
            const passValResponse = await validatePasswordQuery({
                selectedCompanyId,
                username: data.preferredUsername,
                password,
            });
            if (passValResponse.isError) {
                const directError = passValResponse.error;
                const composedError = passValResponse.error;
                const errorMessage = handlePasswordValidationErrorMessages(typeof directError.details === 'string' ? directError.details : composedError?.details?.result?.error);
                setError(errorMessage);
                return false;
            }
            if (passValResponse.isSuccess && passValResponse.data.status === 200) {
                return true;
            }
            setError(LoginErrorsType.GENERIC);
            return false;
        }
        catch (err) {
            const customError = err;
            if (customError) {
                const errorMessage = handlePasswordValidationErrorMessages(customError?.details?.result?.message);
                setError(errorMessage);
            }
            return false;
        }
        finally {
            setLoading(false);
        }
    }, [data, validatePasswordQuery]);
    const storedToken = token || localStorageService.getItemSync(AuthToken);
    const isLoggedIn = !!storedToken;
    const isFullyAuthenticated = isLoggedIn && isDeviceValidatedInSession();
    return {
        login,
        logout,
        sendToken,
        validateToken,
        registerDeviceAndSave,
        validatePassword,
        loading,
        error,
        data,
        token: storedToken,
        isLoggedIn,
        isAuthenticated: isFullyAuthenticated,
        codeAttempts,
    };
}
